import { Stack, Text } from "@certn/cascadia";
import { HoverInfo, List, ListItem } from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";

import { formatMonitoringDate } from "../monitoringFormat";
import {
  getMeaningfulMonitoringRunDetails,
  getMonitoringRunChangeDisplay,
  getVisibleMonitoringRunChanges,
  hasMonitoringRunDetailContent,
} from "../monitoringRunChangeText";
import type { EnrollmentDetailCheck, MonitoringRunDetail } from "../types";
import { RunResultTag } from "./monitoringTags";

export function enrollmentToRunDetail(
  enrollment: EnrollmentDetailCheck
): MonitoringRunDetail {
  return {
    check_type: enrollment.check_type,
    check_label: enrollment.check_label,
    last_run_date: enrollment.last_run_date,
    result_status: enrollment.last_run_result,
    changes: enrollment.last_run_changes,
    alerts: enrollment.alert_notifications
      .map((notification) => notification.information)
      .filter((information) => information.trim().length > 0),
  };
}

function MonitoringRunDetailContent({
  detail,
}: {
  detail: MonitoringRunDetail;
}) {
  const { t } = useTranslation();
  if (!hasMonitoringRunDetailContent(detail)) {
    return null;
  }
  const formattedDate = formatMonitoringDate(detail.last_run_date);
  const resultLine = [detail.check_label, formattedDate]
    .filter(Boolean)
    .join(" · ");
  const visibleChanges = getVisibleMonitoringRunChanges(detail.changes);
  return (
    <Stack gap="space-1">
      <Text style="global/heading-small">{t("Result")}</Text>
      <Text style="form/body-secondary">{resultLine}</Text>
      {visibleChanges.length > 0 && (
        <>
          <Text style="global/heading-small">{t("Change")}</Text>
          <List type="unordered">
            {visibleChanges.map((change, index) => {
              const display = getMonitoringRunChangeDisplay(change, t);
              return (
                <ListItem key={`${change.label}-${index}`}>
                  {display.kind === "no_value" ? (
                    <Text style="form/body-secondary">{display.message}</Text>
                  ) : (
                    <Text style="form/body-secondary">
                      {display.prefix}{" "}
                      <Text style="global/heading-small">
                        {display.toValue}
                      </Text>
                    </Text>
                  )}
                </ListItem>
              );
            })}
          </List>
        </>
      )}
      {detail.alerts.length > 0 && (
        <>
          <Text style="global/heading-small">{t("Alerts")}</Text>
          <List type="unordered">
            {detail.alerts.map((alert, index) => (
              <ListItem key={`${alert}-${index}`}>
                <Text style="form/body-secondary">{alert}</Text>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Stack>
  );
}

export function MonitoringRunResultTagWithTooltip({
  result,
  details,
}: {
  result: string;
  details: readonly MonitoringRunDetail[];
}) {
  const tag = <RunResultTag result={result} />;
  const meaningfulDetails = getMeaningfulMonitoringRunDetails(details);
  if (meaningfulDetails.length === 0) {
    return tag;
  }
  return (
    <HoverInfo
      placement="top"
      testID={`result-${result.toLowerCase().replace(/_/g, "-")}-tooltip`}
      infoContent={
        <Stack gap="space-3">
          {meaningfulDetails.map((detail) => (
            <MonitoringRunDetailContent
              key={detail.check_type}
              detail={detail}
            />
          ))}
        </Stack>
      }
      trigger={tag}
    />
  );
}
